import { StreamLanguage } from '@codemirror/language';
import { shell } from '@codemirror/legacy-modes/mode/shell';

import { useMemo, useState } from 'react';
import { match } from 'ts-pattern';

import { Code, CodeCard } from '@/components/Code/Code';
import { GroupedTabs, Text } from '@/design-system';

import { CreateApiKey } from '../Onboarding/components/TracingQuickstart';

const shellLanguage = StreamLanguage.define(shell);

const tabs = [
  {
    display: 'Python',
    value: 'python',
  },
  {
    display: 'TypeScript',
    value: 'typescript',
  },
];

const pythonSteps = (apiKey: string) => [
  {
    title: 'Install the LangGraph CLI',
    code: 'pip install -U "langgraph-cli[inmem]"',
  },
  {
    title: 'Create a LangGraph app',
    description: (
      <Text>
        Create a new app from the{' '}
        <code className="font-semibold">new-langgraph-project-python</code>{' '}
        template.
      </Text>
    ),
    code: 'langgraph new path/to/your/app --template new-langgraph-project-python',
  },
  {
    title: 'Install dependencies',
    code: `cd path/to/your/app
pip install -e .`,
  },
  {
    title: 'Create a .env file',
    description: (
      <Text>
        You will find a <code className="font-semibold">.env.example</code> in
        the root of your new LangGraph app. Create a{' '}
        <code className="font-semibold">.env</code> file in the root of your new
        LangGraph app and copy the contents of the{' '}
        <code className="font-semibold">.env.example</code> file into it,
        filling in the necessary API keys:
      </Text>
    ),
    code: `LANGSMITH_API_KEY=${apiKey}`,
  },
  {
    title: 'Launch LangGraph Server',
    code: 'langgraph dev',
  },
];

const typescriptSteps = (apiKey: string) => [
  {
    title: 'Create a LangGraph app',
    description: (
      <Text>
        Create a new app from the{' '}
        <code className="font-semibold">new-langgraph-project-js</code>{' '}
        template.
      </Text>
    ),
    code: 'npx @langchain/langgraph-cli new path/to/your/app --template new-langgraph-project-js',
  },
  {
    title: 'Install dependencies',
    code: `cd path/to/your/app
yarn install`,
  },
  {
    title: 'Create a .env file',
    description: (
      <Text>
        You will find a <code className="font-semibold">.env.example</code> in
        the root of your new LangGraph app. Create a{' '}
        <code className="font-semibold">.env</code> file in the root of your new
        LangGraph app and copy the contents of the{' '}
        <code className="font-semibold">.env.example</code> file into it,
        filling in the necessary API keys:
      </Text>
    ),
    code: `LANGSMITH_API_KEY=${apiKey}`,
  },
  {
    title: 'Launch LangGraph Server',
    code: 'npx @langchain/langgraph-cli dev',
  },
];

export function CreateALangGraphApp() {
  const [lang, setLang] = useState<'python' | 'typescript'>('python');
  const [newKey, setNewKey] = useState<string | null>(null);
  const apiKey = newKey ?? '<your-api-key>';

  const steps = useMemo(
    () =>
      match(lang)
        .with('python', () => pythonSteps(apiKey))
        .with('typescript', () => typescriptSteps(apiKey))
        .exhaustive(),
    [lang, apiKey]
  );

  return (
    <div className="h-full py-0">
      <div className="flex h-full grow flex-col items-start gap-4 px-[72px]">
        <div className="flex w-full items-center justify-between gap-2">
          <Text variant="h1" className="text-5xl" weight="normal">
            Create a LangGraph app
          </Text>
          <GroupedTabs
            value={lang}
            onChange={(value) => setLang(value as 'python' | 'typescript')}
            options={tabs}
          />
        </div>
        <Text variant="md" color="quaternary">
          Set up a LangGraph app locally for testing and development
        </Text>
        <div className="w-full rounded-3xl border border-secondary bg-primary text-left shadow-lg">
          <div className="flex flex-col items-stretch gap-2 divide-y divide-secondary">
            <CreateApiKey newKey={newKey} setNewKey={setNewKey} idx={1} />
            {steps.map((step, i) => (
              <div key={`${lang}-${i}`} className="flex gap-4 px-9 py-6">
                <Text variant="h3" color="quaternary" weight="medium">
                  {i + 2}
                </Text>
                <div className="flex w-full flex-col gap-2">
                  <Text variant="h3" color="primary">
                    {step.title}
                  </Text>
                  {step.description}
                  <CodeCard>
                    <Code
                      language={shellLanguage}
                      value={step.code}
                      readOnly
                      showCopyButton
                      variant="plain"
                    />
                  </CodeCard>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
